<template>
  <div class="login-page">
    <!-- Animated background -->
    <div class="login-bg">
      <div class="login-bg__gradient" />
      <div class="login-bg__grid" />
      <div class="login-bg__glow login-bg__glow--1" />
      <div class="login-bg__glow login-bg__glow--2" />
      <div class="login-bg__glow login-bg__glow--3" />
    </div>

    <div class="login-container">
      <!-- Left branding panel -->
      <div class="login-brand">
        <div class="login-brand__content">
          <div class="login-brand__logo-wrap">
            <img
              :src="require('@/assets/images/investidor/cs-logo-login.svg')"
              alt="CreditSec"
              class="login-brand__logo"
            />
          </div>

          <div class="login-brand__text">
            <h1 class="login-brand__title">
              CreditSec
            </h1>
            <p class="login-brand__subtitle">
              Unimos tecnologia para dar autonomia de dados para Investidores, Cedentes e fornecedores parceiros.
            </p>
          </div>

          <div class="login-brand__stats">
            <div class="login-brand__stat">
              <div class="login-brand__stat-icon">
                <feather-icon icon="ShieldIcon" size="20" />
              </div>
              <div>
                <span class="login-brand__stat-label">Seguranca</span>
                <span class="login-brand__stat-value">End-to-end</span>
              </div>
            </div>
            <div class="login-brand__stat">
              <div class="login-brand__stat-icon">
                <feather-icon icon="ClockIcon" size="20" />
              </div>
              <div>
                <span class="login-brand__stat-label">Operacao</span>
                <span class="login-brand__stat-value">24/7</span>
              </div>
            </div>
            <div class="login-brand__stat">
              <div class="login-brand__stat-icon">
                <feather-icon icon="TrendingUpIcon" size="20" />
              </div>
              <div>
                <span class="login-brand__stat-label">Dados</span>
                <span class="login-brand__stat-value">Real-time</span>
              </div>
            </div>
          </div>
        </div>

        <p class="login-brand__footer">
          &copy; {{ currentYear }} CreditSec Group
        </p>
      </div>

      <!-- Right form panel -->
      <div class="login-form-panel">
        <div class="login-form-wrap">
          <!-- Mobile logo -->
          <div class="login-form__mobile-logo d-md-none">
            <img
              :src="require('@/assets/images/credit-sec-logo.svg')"
              alt="CreditSec"
              style="height: 36px;"
            />
          </div>

          <div class="login-form__header">
            <h2 class="login-form__title">
              Bem-vindo de volta
            </h2>
            <p class="login-form__desc">
              Portal do <strong>{{ portalTitle }}</strong>
            </p>
          </div>

          <b-alert
            v-if="errorMessage"
            variant="danger"
            show
            dismissible
            class="mb-2"
            @dismissed="errorMessage = ''"
          >
            {{ errorMessage }}
          </b-alert>

          <validation-observer ref="loginForm" #default="{ invalid }">
            <b-form class="login-form-fields" @submit.prevent="login">
              <div class="login-field">
                <label class="login-field__label">Email</label>
                <validation-provider
                  #default="{ errors }"
                  name="Email"
                  vid="email"
                  rules="required|email"
                >
                  <b-form-input
                    v-model="userEmail"
                    :state="errors.length > 0 ? false : null"
                    placeholder="nome@empresa.com"
                    type="email"
                    autofocus
                    class="login-input"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </div>

              <div class="login-field">
                <div class="d-flex justify-content-between align-items-center">
                  <label class="login-field__label mb-0">Senha</label>
                  <span
                    class="login-forgot-link"
                    @click="$bvModal.show('resetPassword')"
                  >
                    Esqueci minha senha
                  </span>
                </div>
                <validation-provider
                  #default="{ errors }"
                  name="Senha"
                  vid="password"
                  rules="required"
                >
                  <b-input-group class="input-group-merge login-input-group">
                    <b-form-input
                      v-model="password"
                      :state="errors.length > 0 ? false : null"
                      :type="passwordFieldType"
                      placeholder="Digite sua senha"
                      autocomplete="current-password"
                      class="login-input"
                    />
                    <b-input-group-append is-text>
                      <feather-icon
                        class="cursor-pointer"
                        :icon="passwordToggleIcon"
                        @click="togglePasswordVisibility('passwordFieldType')"
                      />
                    </b-input-group-append>
                  </b-input-group>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </div>

              <b-form-checkbox
                id="remember-me"
                v-model="rememberMe"
                class="login-remember mb-2"
              >
                Lembrar-me
              </b-form-checkbox>

              <b-button
                type="submit"
                block
                :disabled="invalid || loading"
                class="login-btn"
              >
                <feather-icon v-if="!loading" icon="LogInIcon" size="18" class="mr-50" />
                <b-spinner v-if="loading" small class="mr-50" />
                {{ loading ? 'Entrando...' : 'Entrar' }}
              </b-button>
            </b-form>
          </validation-observer>

          <p class="login-form__secure">
            <feather-icon icon="LockIcon" size="14" />
            Conexao segura e criptografada
          </p>
        </div>
      </div>
    </div>

    <!-- Reset password modal -->
    <b-modal id="resetPassword" centered hide-footer header-bg-variant="white">
      <div class="d-flex flex-column justify-content-center align-items-start">
        <h1 class="mb-1">Trocar senha</h1>
        <p class="h5 mb-2">
          Identifique-se para receber um email com as instrucoes e link para criar uma nova senha.
        </p>

        <validation-observer ref="resetForm" #default="{ invalid }" class="w-100">
          <b-form-group label="Email" label-for="reset-email" class="d-flex flex-column mb-3">
            <validation-provider
              #default="{ errors }"
              name="Email"
              vid="reset-email"
              rules="required|email"
            >
              <b-form-input
                id="reset-email"
                v-model="resetPasswordData.email"
                :state="errors.length > 0 ? false : null"
                class="w-100"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <div class="d-flex flex-row justify-content-around mt-1">
            <b-button
              variant="outline-primary"
              class="mb-1 mr-1"
              @click="$bvModal.hide('resetPassword')"
            >
              Cancelar
            </b-button>
            <b-button
              variant="primary"
              class="mb-1"
              :disabled="invalid"
              @click="sendResetPasswordEmail"
            >
              Enviar link
            </b-button>
          </div>
        </validation-observer>
      </div>
    </b-modal>

    <b-modal id="resetSuccesfullyMessage" centered hide-footer header-bg-variant="white">
      <div class="p-2">
        <div class="d-flex flex-column justify-content-center align-items-start">
          <h1 class="mb-1">Link enviado para o email digitado</h1>
          <p class="mb-2">
            Verifique o seu email. Enviamos as instrucoes e um link para voce refazer a sua senha,
            para o email <strong>{{ resetPasswordData.email }}</strong>, que voce digitou.
          </p>
          <p class="mb-2">
            Caso voce nao receba o email em alguns minutos, verifique a sua caixa de spam ou refaca
            esse processo.
          </p>
        </div>
        <div>
          <b-button variant="primary" @click="closeReset"> Fechar </b-button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import {
  BForm,
  BFormGroup,
  BFormInput,
  BInputGroup,
  BInputGroupAppend,
  BFormCheckbox,
  BButton,
  BAlert,
  BSpinner,
} from 'bootstrap-vue'
import { required, email } from '@validations'
import { togglePasswordVisibility } from '@core/mixins/ui/forms'
import { getHomeRouteForLoggedInUser } from '@/auth/utils'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  components: {
    BForm,
    BFormGroup,
    BFormInput,
    BInputGroup,
    BInputGroupAppend,
    BFormCheckbox,
    BButton,
    BAlert,
    BSpinner,
    ValidationProvider,
    ValidationObserver,
  },
  mixins: [togglePasswordVisibility],
  data() {
    return {
      loading: false,
      password: '',
      userEmail: '',
      rememberMe: false,
      errorMessage: '',
      required,
      email,
      resetPasswordData: {
        email: '',
      },
    }
  },
  computed: {
    passwordToggleIcon() {
      return this.passwordFieldType === 'password' ? 'EyeIcon' : 'EyeOffIcon'
    },
    currentYear() {
      return new Date().getFullYear()
    },
    portalTitle() {
      if (this.$route.meta?.layoutPage?.formCard?.title) {
        return this.$route.meta.layoutPage.formCard.title
      }
      return 'Administrador'
    },
  },
  methods: {
    closeReset() {
      this.$bvModal.hide('resetSuccesfullyMessage')
      this.$bvModal.hide('resetPassword')
      this.resetPasswordData.email = ''
    },
    async login() {
      try {
        this.loading = true
        this.errorMessage = ''
        const success = await this.$refs.loginForm.validate()
        if (!success) return

        const { data } = await this.$store.dispatch('auth/login', {
          email: this.userEmail,
          password: this.password,
        })

        this.$ability.update(data.user.ability || [])

        if (data.user.type === 'cedente')
          await this.$store.dispatch('auth/getCurrentEconomicGroupData')

        this.$router.replace(getHomeRouteForLoggedInUser(data.user.type)).then(() => {
          this.$toast({
            component: ToastificationContent,
            position: 'top-right',
            props: {
              title: `Boas vindas ${data.user.nome}`,
              icon: 'CoffeeIcon',
              avatarVariant: 'success',
              titleVariant: 'success',
              text: 'Login efetuado com sucesso!',
            },
          })
        })
      } catch (error) {
        this.errorMessage = 'Email ou senha invalidos. Tente novamente.'
      } finally {
        this.loading = false
      }
    },
    async sendResetPasswordEmail() {
      try {
        const success = await this.$refs.resetForm.validate()
        if (!success) return
        this.$swal.showLoading()
        await this.$store.dispatch('auth/sendResetPasswordEmail', {
          email: this.resetPasswordData.email,
        })
        this.$swal.close()
        this.$bvModal.hide('resetPassword')
        this.$bvModal.show('resetSuccesfullyMessage')
      } catch (error) {
        console.log(error)
        this.$swal({
          title: 'Erro!',
          text: error.response?.data || 'Erro ao enviar email',
          icon: 'error',
          confirmButtonText: 'Ok',
        })
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.login-page {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: #0a0e17;
}

// -- Animated background --
.login-bg {
  position: absolute;
  inset: 0;
  z-index: 0;
  overflow: hidden;

  &__gradient {
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, #0a0e17 0%, #111827 40%, #0f172a 100%);
  }

  &__grid {
    position: absolute;
    inset: 0;
    background-image:
      linear-gradient(rgba(46, 196, 164, 0.03) 1px, transparent 1px),
      linear-gradient(90deg, rgba(46, 196, 164, 0.03) 1px, transparent 1px);
    background-size: 60px 60px;
    mask-image: radial-gradient(ellipse 80% 80% at 30% 50%, black, transparent);
    -webkit-mask-image: radial-gradient(ellipse 80% 80% at 30% 50%, black, transparent);
  }

  &__glow {
    position: absolute;
    border-radius: 50%;
    filter: blur(120px);
    opacity: 0.4;

    &--1 {
      width: 600px;
      height: 600px;
      background: radial-gradient(circle, rgba(46, 196, 164, 0.15), transparent 70%);
      top: -200px;
      left: -100px;
      animation: float1 20s ease-in-out infinite;
    }

    &--2 {
      width: 400px;
      height: 400px;
      background: radial-gradient(circle, rgba(26, 31, 46, 0.4), transparent 70%);
      bottom: -100px;
      right: -50px;
      animation: float2 25s ease-in-out infinite;
    }

    &--3 {
      width: 300px;
      height: 300px;
      background: radial-gradient(circle, rgba(46, 196, 164, 0.08), transparent 70%);
      top: 50%;
      left: 30%;
      animation: float3 18s ease-in-out infinite;
    }
  }
}

@keyframes float1 {
  0%, 100% { transform: translate(0, 0) scale(1); }
  33% { transform: translate(30px, 40px) scale(1.05); }
  66% { transform: translate(-20px, 20px) scale(0.95); }
}

@keyframes float2 {
  0%, 100% { transform: translate(0, 0) scale(1); }
  50% { transform: translate(-40px, -30px) scale(1.1); }
}

@keyframes float3 {
  0%, 100% { transform: translate(0, 0); }
  50% { transform: translate(50px, -40px); }
}

// -- Container --
.login-container {
  position: relative;
  z-index: 1;
  display: flex;
  width: 100%;
  max-width: 1100px;
  min-height: 640px;
  margin: 24px;
  border-radius: 24px;
  overflow: hidden;
  box-shadow:
    0 0 0 1px rgba(255, 255, 255, 0.06),
    0 24px 80px -12px rgba(0, 0, 0, 0.5),
    0 0 120px -40px rgba(46, 196, 164, 0.12);
  animation: containerReveal 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards;

  @media (max-width: 960px) {
    flex-direction: column;
    min-height: auto;
    max-width: 480px;
    margin: 16px;
  }
}

@keyframes containerReveal {
  from {
    opacity: 0;
    transform: translateY(20px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

// -- Brand panel --
.login-brand {
  flex: 0 0 440px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 48px 40px;
  background: linear-gradient(160deg, #131926 0%, #0d1118 100%);
  border-right: 1px solid rgba(255, 255, 255, 0.04);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(46, 196, 164, 0.3), transparent);
  }

  @media (max-width: 960px) {
    display: none;
  }

  &__content {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  &__logo-wrap {
    margin-bottom: 48px;
    animation: fadeDown 0.6s ease-out 0.3s both;
  }

  &__logo {
    height: 42px;
    width: auto;
  }

  &__text {
    margin-bottom: 48px;
    animation: fadeDown 0.6s ease-out 0.45s both;
  }

  &__title {
    font-size: 2rem;
    font-weight: 700;
    color: #fff;
    letter-spacing: -0.02em;
    line-height: 1.2;
    margin-bottom: 16px;
    color: #fff;
  }

  &__subtitle {
    font-size: 1rem;
    color: rgba(255, 255, 255, 0.45);
    line-height: 1.6;
    max-width: 320px;
  }

  &__stats {
    display: flex;
    flex-direction: column;
    gap: 16px;
    animation: fadeDown 0.6s ease-out 0.6s both;
  }

  &__stat {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 14px 16px;
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.03);
    border: 1px solid rgba(255, 255, 255, 0.04);
    transition: all 0.3s ease;

    &:hover {
      background: rgba(46, 196, 164, 0.06);
      border-color: rgba(46, 196, 164, 0.12);
      transform: translateX(4px);
    }
  }

  &__stat-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background: rgba(46, 196, 164, 0.1);
    color: #2ec4a4;
    flex-shrink: 0;
  }

  &__stat-label {
    display: block;
    font-size: 0.75rem;
    color: rgba(255, 255, 255, 0.35);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-bottom: 2px;
  }

  &__stat-value {
    display: block;
    font-size: 0.95rem;
    color: rgba(255, 255, 255, 0.85);
    font-weight: 600;
  }

  &__footer {
    font-size: 0.8rem;
    color: rgba(255, 255, 255, 0.2);
    animation: fadeDown 0.6s ease-out 0.75s both;
  }
}

@keyframes fadeDown {
  from {
    opacity: 0;
    transform: translateY(-12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

// -- Form panel --
.login-form-panel {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px 40px;
  background: #fff;
  position: relative;

  @media (max-width: 960px) {
    padding: 32px 24px;
  }
}

.login-form-wrap {
  width: 100%;
  max-width: 380px;
  animation: formReveal 0.6s ease-out 0.4s both;
}

@keyframes formReveal {
  from {
    opacity: 0;
    transform: translateX(16px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.login-form {
  &__mobile-logo {
    margin-bottom: 32px;
    text-align: center;
  }

  &__header {
    margin-bottom: 36px;
  }

  &__title {
    font-size: 1.75rem;
    font-weight: 700;
    color: #111827;
    letter-spacing: -0.02em;
    margin-bottom: 8px;
  }

  &__desc {
    font-size: 0.95rem;
    color: #6b7280;
    line-height: 1.5;

    strong {
      color: #2ec4a4;
      font-weight: 600;
    }
  }

  &__secure {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    margin-top: 32px;
    font-size: 0.8rem;
    color: #9ca3af;
  }
}

// -- Form fields --
.login-field {
  margin-bottom: 20px;

  &__label {
    display: block;
    font-size: 0.85rem;
    font-weight: 600;
    color: #374151;
    margin-bottom: 8px;
    letter-spacing: 0.01em;
  }
}

.login-forgot-link {
  font-size: 0.8rem;
  color: #2ec4a4;
  cursor: pointer;
  font-weight: 500;
  margin-bottom: 8px;

  &:hover {
    color: darken(#2ec4a4, 10%);
    text-decoration: underline;
  }
}

// -- Input styling --
.login-input {
  border: 1px solid #d1d5db !important;
  border-radius: 8px !important;
  padding: 10px 14px !important;
  height: auto !important;
  font-size: 0.95rem !important;
  color: #111827 !important;
  background: #fff !important;
  transition: border-color 0.2s ease, box-shadow 0.2s ease !important;

  &:focus {
    border-color: #2ec4a4 !important;
    box-shadow: 0 0 0 3px rgba(46, 196, 164, 0.1) !important;
  }

  &::placeholder {
    color: #9ca3af !important;
  }
}

.login-input-group {
  .input-group-text {
    border: 1px solid #d1d5db !important;
    border-left: 0 !important;
    border-radius: 0 8px 8px 0 !important;
    background: #fff !important;
    color: #6b7280 !important;
  }

  .login-input {
    border-right: 0 !important;
    border-radius: 8px 0 0 8px !important;
  }

  &:focus-within {
    .login-input {
      border-color: #2ec4a4 !important;
      box-shadow: none !important;
    }
    .input-group-text {
      border-color: #2ec4a4 !important;
    }
  }
}

// -- Remember checkbox --
.login-remember {
  ::v-deep .custom-control-label {
    color: #4b5563;
    font-size: 0.9rem;
  }

  ::v-deep .custom-control-input:checked ~ .custom-control-label::before {
    background-color: #2ec4a4;
    border-color: #2ec4a4;
  }
}

// -- Submit button --
.login-btn {
  background: linear-gradient(135deg, #1a1f2e 0%, #111827 100%) !important;
  color: #fff !important;
  font-weight: 600 !important;
  letter-spacing: 0.02em !important;
  border-radius: 12px !important;
  height: 52px !important;
  font-size: 0.95rem !important;
  text-transform: none !important;
  border: none !important;
  box-shadow: 0 4px 16px -4px rgba(26, 31, 46, 0.3) !important;
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1) !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;

  &:hover {
    box-shadow: 0 8px 24px -4px rgba(26, 31, 46, 0.4) !important;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.7;
  }
}
</style>
